import { Icon } from '@iconify/react';
import {
  Button,
  Chip,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Tooltip,
} from '@nextui-org/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTagSpecs } from '../../../hooks/queries/common';
import {
  useCreateOrUpdateTag,
  useDeleteTag,
} from '../../../hooks/queries/tags';
import type { TagData, TagSpecAllowedValue } from '../../../types/tag';
import { useTenantStore } from '../../../store/use-tenant-store';

interface TagsModalProps {
  isOpen: boolean;
  onClose: () => void;
  refetchPrompt: () => void;
  tags: TagData[];
  agent: Record<string, string>;
}

export default function TagsModal({
  isOpen,
  onClose,
  refetchPrompt,
  tags,
  agent,
}: TagsModalProps) {
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<Partial<TagData>>({
    tag_spec_id: 0,
    value: '',
    entity_type: '',
    entity_id: 0,
  });
  const [selectedTag, setSelectedTag] = useState<TagData | null>(null);
  const [localTags, setLocalTags] = useState<TagData[]>(tags);
  const tenantConfig = useTenantStore((state) => state.config);
  const { mutateAsync: createOrUpdateTag, isPending: isSaving } =
    useCreateOrUpdateTag({
      onSuccess: () => {
        setIsEditMode(false);
        refetchPrompt?.();
      },
    });

  const { mutateAsync: deleteTag, isPending: isDeleting } = useDeleteTag({
    onSuccess: () => {
      refetchPrompt?.();
    },
  });

  useEffect(() => {
    if (isOpen) {
      setLocalTags(tags);
    }
  }, [isOpen, tags]);

  const { data: tagSpecs } = useTagSpecs();

  const availableTagNames = useMemo((): TagSpecAllowedValue[] => {
    const selectedSpec = tagSpecs?.results.find(
      (spec) => spec.id === formData.tag_spec_id
    );
    return selectedSpec?.constraints.allowed_values ?? [];
  }, [tagSpecs?.results, formData.tag_spec_id]);

  // Helper function to get tab label for a tag
  const getTabLabelForTag = (tagValue: string) => {
    const tab = tenantConfig?.dashboard_config?.tabs?.find(
      (tab) => tab.tag === tagValue
    );
    return tab?.label;
  };

  const handleSave = async () => {
    const data = {
      tag_spec_id: formData.tag_spec_id,
      value: formData.value,
      entity_type: 'agentos.prompt',
      entity_id: Number(agent.id),
    };

    await createOrUpdateTag({
      id: selectedTag?.id,
      data,
    });

    setFormData({ tag_spec_id: 0, value: '', entity_type: '', entity_id: 0 });
    setSelectedTag(null);
  };

  const handleEdit = (tag: TagData) => {
    setSelectedTag(tag);
    setFormData({
      tag_spec_id: tag.tag_spec_id,
      value: tag.value,
      entity_type: tag.entity_type,
      entity_id: tag.entity_id,
    });
  };

  const handleDelete = async (id: number) => {
    await deleteTag(id);
    setLocalTags((current) => current.filter((tag) => tag.id !== id));

    if (selectedTag?.id === id) {
      setSelectedTag(null);
      setIsEditMode(false);
      setFormData({
        tag_spec_id: 0,
        value: '',
        entity_type: '',
        entity_id: 0,
      });
    }
  };

  const getDisplayValue = (value: string) => {
    const tabLabel = getTabLabelForTag(value);
    return tabLabel ? `${value} (${tabLabel})` : value;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      placement="center"
      hideCloseButton={true}
      isDismissable={!isDeleting}
      isKeyboardDismissDisabled={isDeleting}
    >
      <ModalContent>
        <ModalHeader className="flex justify-between items-center">
          <span>
            {isEditMode
              ? `${formData?.tag_spec_id ? 'Edit tag' : 'Create tag'}`
              : 'Tags'}
          </span>
          <Button
            isIconOnly
            variant="light"
            onPress={() => (isEditMode ? setIsEditMode(false) : onClose())}
          >
            {/* Close */}
            <Icon icon="mdi:close" className="h-5 w-5" />
          </Button>
        </ModalHeader>

        <ModalBody>
          {isEditMode ? (
            <div className="space-y-4">
              <Select
                label="Tag Specification"
                selectedKeys={
                  formData.tag_spec_id
                    ? [String(formData.tag_spec_id)]
                    : []
                }
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    tag_spec_id: parseInt(e.target.value as string, 10),
                    value: '',
                  });
                }}
              >
                {(tagSpecs?.results ?? []).map((spec) => (
                  <SelectItem key={String(spec.id)} textValue={spec.label}>
                    {spec.label}
                  </SelectItem>
                ))}
              </Select>

              <Select
                label="Tag Name"
                isDisabled={!formData.tag_spec_id}
                selectedKeys={formData.value ? [formData.value] : []}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    value: e.target.value as string,
                  });
                }}
              >
                {availableTagNames.map((tagName) => (
                  <SelectItem
                    key={tagName.value}
                    textValue={getDisplayValue(tagName.value)}
                  >
                    {getDisplayValue(tagName.value)}
                  </SelectItem>
                ))}
              </Select>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {localTags?.length === 0 && (
                <div className="flex justify-center align-center w-full">
                  No tags
                </div>
              )}
              {localTags?.map((tag) => {
                const tabLabel = getTabLabelForTag(tag.value);
                return (
                  <Chip
                    key={tag.id}
                    variant="flat"
                    endContent={
                      <div className="flex gap-1">
                        <Tooltip content="Edit tag">
                          <button
                            type="button"
                            className="inline-flex"
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              setIsEditMode(true);
                              handleEdit(tag);
                            }}
                          >
                            <Icon icon="mdi:pencil" className="h-4 w-4 cursor-pointer" />
                          </button>
                        </Tooltip>
                        <Tooltip content="Delete tag">
                          <button
                            type="button"
                            className="inline-flex"
                            disabled={isDeleting}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              void handleDelete(tag.id);
                            }}
                          >
                            <Icon
                              icon="mdi:close"
                              className="h-4 w-4"
                            />
                          </button>
                        </Tooltip>
                      </div>
                    }
                  >
                    <div className="flex align-center gap-1">
                      <span className="text-sm font-medium">{tag.value}</span>
                      {tabLabel && (
                        <span className="text-sm text-default-500">
                          ({tabLabel})
                        </span>
                      )}
                    </div>
                  </Chip>
                );
              })}
            </div>
          )}
        </ModalBody>

        <ModalFooter className="justify-between border-t mt-4">
          {isEditMode && (
            <React.Fragment>
              <Button
                variant="bordered"
                onPress={() => (isEditMode ? setIsEditMode(false) : onClose())}
              >
                Cancel
              </Button>
              <Button color="primary" onPress={handleSave} isLoading={isSaving}>
                {isEditMode && selectedTag?.value ? 'Update' : 'Save'}
              </Button>
            </React.Fragment>
          )}
          {!isEditMode && (
            <React.Fragment>
              <Button variant="bordered" onPress={() => onClose()}>
                Cancel
              </Button>
              <Button
                startContent={<Icon icon="mdi:plus" />}
                color="primary"
                onPress={() => {
                  setSelectedTag(null);
                  setFormData({
                    tag_spec_id: 0,
                    value: '',
                    entity_type: '',
                    entity_id: 0,
                  });
                  setIsEditMode(true);
                }}
              >
                Add Tag
              </Button>
            </React.Fragment>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
